/**
 * `ImportPlanV1` (CS r4a deliverable 101): what 2.A stage s3's `buildPlan` makes of a Notion export
 * and 2.B-1 stores as an asset with its hash. It names databases, properties and pages by their
 * Notion ids (32 lowercase hexadecimal digits, 2.A's `canonicalNotionId` form) and holds no body:
 * 2.B-1 re-reads a page with 2.A's `readPage`, which gives the same hash. A warning is a code with
 * an optional handle (a block id, a Notion id or an entry index), never text.
 *
 * Beyond the field shapes the schema holds the plan's references: databases and pages each unique
 * by id; a page's database and parent in the plan; each referenced id (a relation's target, a
 * relation cell's ids) in the plan or named by the page's own `relation_target_missing` warning; a
 * row's relation a relation property of its database; a property's relation database in the plan,
 * its two-way partner a relation property of that database pointing back; the parent chain a tree
 * (no loop) whose depths follow it. 2.A defines what the hashes cover, the order and the tree rule
 * (the depth rule here is the one its deliverable 24 and these goldens state).
 *
 * CS r4c (deliverable 127): an optional `singlePages` describes every page of a `markdown_pages`
 * export (the type proposed for them and their properties); when present, a free page's cells and
 * relations are held to its properties.
 */
import { IMPORT_EXPORT_KINDS, IMPORT_PROPERTY_KINDS, IMPORT_WARNING_CODES, } from '../domain/rows-enums.js';
import { Uuid } from '../dto/common.js';
import { TypeKey } from '../dto/documents.js';
import { LinkTypeKeyText } from '../dto/links.js';
import { z } from '../zod.js';
/** A Notion page or database id as the plan writes it: 32 lowercase hexadecimal digits. */
export const NOTION_ID_PATTERN = /^[0-9a-f]{32}$/;
export const NotionId = z.string().regex(NOTION_ID_PATTERN);
/** A SHA-256 in lowercase hexadecimal (`planHash`, `pageHash`, an upload's digest). */
export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
export const ImportExportKind = z.enum(IMPORT_EXPORT_KINDS);
export const ImportWarningCode = z.enum(IMPORT_WARNING_CODES);
export const ImportPropertyKind = z.enum(IMPORT_PROPERTY_KINDS);
/** A count in a plan or a report. */
const Count = z.number().int().nonnegative();
/**
 * A warning: its code and, where one applies, what it is about: a block id, a Notion id or a zip
 * entry's index. The handle is never text from the export.
 */
export const ImportWarningV1 = z.strictObject({
    code: ImportWarningCode,
    handle: z.union([Uuid, NotionId, Count]).optional(),
});
/** A title as the export gives it: possibly empty (an untitled page), bounded. */
const PlanTitle = z.string().max(2000);
/** A zip entry's path, `/`-separated. */
const EntryPath = z.string().min(1).max(4096);
/** A Notion property's name, as the export spells it (re-import maps properties by it). */
export const NotionPropertyName = z.string().min(1).max(500);
/** The plan's totals, with every warning counted by code. */
export const ImportPlanStatsV1 = z.strictObject({
    databases: Count,
    pages: Count,
    relations: Count,
    images: Count,
    warnings: z.partialRecord(ImportWarningCode, Count),
});
/** One Notion property of a database and what the plan proposes for it. */
export const ImportPlanPropertyV1 = z.strictObject({
    notionName: NotionPropertyName,
    proposedKind: ImportPropertyKind,
    /** A select's, multi-select's or status's options, in the export's order. */
    options: z.array(z.string().max(500)).max(1000),
    /** The database a relation's targets belong to, when one holds them all. */
    relationDatabaseId: NotionId.optional(),
    /** The partner of a two-way relation: its `notionName` in `relationDatabaseId`'s database. */
    twoWayPartner: NotionPropertyName.optional(),
    proposedLinkType: z
        .strictObject({
        key: LinkTypeKeyText,
        name: z.string().min(1).max(200),
        inverseName: z.string().min(1).max(200).optional(),
    })
        .optional(),
});
/** The type the plan proposes for a database's rows, or for an export's single pages. */
const ImportPlanProposedTypeV1 = z.strictObject({
    key: TypeKey,
    name: z.string().min(1).max(200),
    /** True when an existing type was proposed (2.A's `suggestTypeFor`). */
    existing: z.boolean(),
});
/** One Notion database: its entry, the type proposed for it and its properties. */
export const ImportPlanDatabaseV1 = z.strictObject({
    notionId: NotionId,
    title: PlanTitle,
    entry: EntryPath,
    proposedType: ImportPlanProposedTypeV1,
    properties: z.array(ImportPlanPropertyV1).max(500),
});
/**
 * The single pages of a `markdown_pages` export (CS r4c deliverable 127; amendments 2.2 §5,
 * "Input modes" and "Property grammar"): the type proposed for every page, and the properties
 * their property blocks hold, each once by `notionName`. A pages export holds no database, so no
 * property names a relation database or a two-way partner.
 */
export const ImportPlanSinglePagesV1 = z.strictObject({
    proposedType: ImportPlanProposedTypeV1,
    properties: z.array(ImportPlanPropertyV1).max(500),
});
/** A property's value on a page, as 2.A reads its cell: the text and what it names. */
export const ImportPlanPropValueV1 = z.strictObject({
    text: z.string().max(100_000),
    /** A multi-valued cell's items (a multi-select's options, a list). */
    items: z.array(z.string().max(2000)).max(1000).optional(),
    /** The Notion ids a relation cell names. */
    notionIds: z.array(NotionId).max(1000).optional(),
    /** The zip entries a files cell names. */
    entries: z.array(EntryPath).max(1000).optional(),
});
/** One relation value: the property, the target page and the label the export shows for it. */
export const ImportPlanRelationV1 = z.strictObject({
    notionName: NotionPropertyName,
    targetId: NotionId,
    label: PlanTitle,
});
/** A link in the body to another Notion page (1.H's `PageLink`, the id canonical or null). */
export const ImportPlanLinkV1 = z.strictObject({
    notionId: NotionId.nullable(),
    title: PlanTitle,
    blockIds: z.array(Uuid),
});
/** An image of the body, with its zip entry (null when the file is missing from the export). */
export const ImportPlanImageV1 = z.strictObject({
    pendingId: Uuid,
    blockId: Uuid,
    entry: EntryPath.nullable(),
});
/** The deepest a page sits in the plan's tree: deeper pages hang from their depth-32 ancestor. */
export const IMPORT_MAX_DEPTH = 32;
/** One page of the export, in the plan's order (`ordinal`). */
export const ImportPlanPageV1 = z.strictObject({
    notionId: NotionId,
    title: PlanTitle,
    entry: EntryPath,
    /** The database the page is a row of, or null for a free page. */
    databaseId: NotionId.nullable(),
    /** The page whose folder holds it (a row's: its database's parent), or null at the top. */
    parentId: NotionId.nullable(),
    depth: z.number().int().min(0).max(IMPORT_MAX_DEPTH),
    ordinal: Count,
    pageHash: Sha256Hex,
    wordCount: Count,
    props: z.record(NotionPropertyName, ImportPlanPropValueV1),
    relations: z.array(ImportPlanRelationV1),
    links: z.array(ImportPlanLinkV1),
    images: z.array(ImportPlanImageV1),
    warnings: z.array(ImportWarningV1),
});
const head = {
    version: z.literal(1),
    source: z.literal('notion'),
    exportKind: ImportExportKind,
    planHash: Sha256Hex,
    stats: ImportPlanStatsV1,
    databases: z.array(ImportPlanDatabaseV1),
    /**
     * Only on a `markdown_pages` plan, and optional (CS r4c): absent, a plan parses as before (stored
     * plans, 2.A's output until its filler lands, open point 57). `planHash` need not cover it: each
     * `pageHash` covers its page's cells.
     */
    singlePages: ImportPlanSinglePagesV1.optional(),
};
/**
 * `singlePages` only on a `markdown_pages` plan; its property names each once; none naming a
 * relation database or a two-way partner (CS r4c deliverable 127).
 */
function checkSinglePages(plan, ctx) {
    const { singlePages } = plan;
    if (singlePages === undefined)
        return;
    if (plan.exportKind !== 'markdown_pages') {
        ctx.addIssue({
            code: 'custom',
            message: 'singlePages is only for single Markdown pages',
            path: ['singlePages'],
        });
    }
    const names = new Set();
    singlePages.properties.forEach((property, j) => {
        const at = ['singlePages', 'properties', j];
        if (names.has(property.notionName)) {
            ctx.addIssue({
                code: 'custom',
                message: 'a property name appears twice in singlePages',
                path: [...at, 'notionName'],
            });
        }
        names.add(property.notionName);
        if ((property.relationDatabaseId ?? property.twoWayPartner) !== undefined) {
            ctx.addIssue({
                code: 'custom',
                message: 'a single-page property names no relation database or two-way partner',
                path: [
                    ...at,
                    property.relationDatabaseId === undefined ? 'twoWayPartner' : 'relationDatabaseId',
                ],
            });
        }
    });
}
/** Unique database ids; each relation database in the plan; each two-way partner pointing back. */
function checkDatabases(databases, ctx) {
    const byId = new Map();
    databases.forEach((database, i) => {
        if (byId.has(database.notionId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'a database id appears twice',
                path: ['databases', i, 'notionId'],
            });
        }
        byId.set(database.notionId, database);
    });
    databases.forEach((database, i) => {
        const names = new Set();
        database.properties.forEach((property, j) => {
            const at = ['databases', i, 'properties', j];
            if (names.has(property.notionName)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'a property name appears twice in one database',
                    path: [...at, 'notionName'],
                });
            }
            names.add(property.notionName);
            const relational = property.proposedKind === 'relation';
            if (!relational && (property.relationDatabaseId ?? property.twoWayPartner) !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'only a relation names a relation database or a two-way partner',
                    path: [...at, 'proposedKind'],
                });
            }
            if (property.relationDatabaseId === undefined) {
                if (property.twoWayPartner !== undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'a two-way partner needs the relation database',
                        path: [...at, 'twoWayPartner'],
                    });
                }
                return;
            }
            const target = byId.get(property.relationDatabaseId);
            if (target === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'the relation database is not in the plan',
                    path: [...at, 'relationDatabaseId'],
                });
                return;
            }
            if (property.twoWayPartner === undefined)
                return;
            const partner = target.properties.find((p) => p.notionName === property.twoWayPartner);
            if (partner?.proposedKind !== 'relation' ||
                partner.relationDatabaseId !== database.notionId ||
                partner.twoWayPartner !== property.notionName) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'the two-way partner is not a relation of that database pointing back',
                    path: [...at, 'twoWayPartner'],
                });
            }
        });
    });
}
/**
 * The pages whose parent chain comes back to them through two or more pages (a page that is its
 * own parent is refused apart). Each chain is walked once.
 */
function pagesInLoops(byId) {
    const walked = new Set();
    const looped = new Set();
    for (const start of byId.keys()) {
        const path = [];
        const onPath = new Set();
        let id = start;
        while (id !== null && !walked.has(id) && !onPath.has(id)) {
            const page = byId.get(id);
            if (page === undefined)
                break;
            path.push(id);
            onPath.add(id);
            id = page.parentId;
        }
        if (id !== null && onPath.has(id)) {
            for (const member of path.slice(path.indexOf(id)))
                looped.add(member);
        }
        for (const member of path)
            walked.add(member);
    }
    return looped;
}
/**
 * The depth the chain gives a page: 0 at the top, one below its parent, and a database row one
 * further down, under its database (2.A deliverable 24: a row carries its database's parent). Past
 * `IMPORT_MAX_DEPTH` the page hangs at it, flattened.
 */
function depthByChain(page, parent) {
    return (parent === undefined ? 0 : parent.depth + 1) + (page.databaseId === null ? 0 : 1);
}
/**
 * Unique page ids; each database and parent in the plan; each referenced id (a relation's target, a
 * relation cell's ids) in the plan or named by the page's own `relation_target_missing`; each
 * relation a relation property of the page's database; the parent chain a tree and each depth the
 * chain's.
 */
function checkPages(pages, databases, singlePages, ctx) {
    const databaseById = new Map(databases.map((d) => [d.notionId, d]));
    const singleByName = new Map((singlePages?.properties ?? []).map((p) => [p.notionName, p]));
    const pageById = new Map();
    pages.forEach((page, i) => {
        if (pageById.has(page.notionId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'a page id appears twice',
                path: ['pages', i, 'notionId'],
            });
            return;
        }
        pageById.set(page.notionId, page);
    });
    const looped = pagesInLoops(pageById);
    pages.forEach((page, i) => {
        const at = ['pages', i];
        const database = page.databaseId === null ? undefined : databaseById.get(page.databaseId);
        if (page.databaseId !== null && database === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: "the page's database is not in the plan",
                path: [...at, 'databaseId'],
            });
        }
        const parent = page.parentId === null ? undefined : pageById.get(page.parentId);
        if (page.parentId !== null && parent === undefined) {
            ctx.addIssue({
                code: 'custom',
                message: "the page's parent is not in the plan",
                path: [...at, 'parentId'],
            });
        }
        if (page.parentId === page.notionId) {
            ctx.addIssue({
                code: 'custom',
                message: 'a page is not its own parent',
                path: [...at, 'parentId'],
            });
        }
        else if (looped.has(page.notionId)) {
            ctx.addIssue({
                code: 'custom',
                message: 'the parent chain loops',
                path: [...at, 'parentId'],
            });
        }
        if ((page.parentId === null || parent !== undefined) && !looped.has(page.notionId)) {
            const expected = depthByChain(page, parent);
            const flattened = page.warnings.some((w) => w.code === 'tree_flattened');
            const agrees = expected <= IMPORT_MAX_DEPTH
                ? page.depth === expected
                : page.depth === IMPORT_MAX_DEPTH && flattened;
            if (!agrees) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'the depth does not follow the parent chain',
                    path: [...at, 'depth'],
                });
            }
        }
        const missing = new Set(page.warnings
            .filter((w) => w.code === 'relation_target_missing' && typeof w.handle === 'string')
            .map((w) => w.handle));
        const resolved = (id) => pageById.has(id) || missing.has(id);
        // CS r4c: with `singlePages`, a free page's cells are of its properties.
        const single = singlePages !== undefined && page.databaseId === null;
        for (const name of single ? Object.keys(page.props) : []) {
            if (!singleByName.has(name)) {
                ctx.addIssue({
                    code: 'custom',
                    message: "a free page's property is not one of singlePages' properties",
                    path: [...at, 'props', name],
                });
            }
        }
        for (const [name, value] of Object.entries(page.props)) {
            value.notionIds?.forEach((id, k) => {
                if (!resolved(id)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: 'a relation cell names a page neither in the plan nor warned relation_target_missing',
                        path: [...at, 'props', name, 'notionIds', k],
                    });
                }
            });
        }
        page.relations.forEach((relation, j) => {
            if (!resolved(relation.targetId)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'a relation target is neither in the plan nor warned relation_target_missing',
                    path: [...at, 'relations', j, 'targetId'],
                });
            }
            // A free page has no database whose properties could name it (a `markdown_pages` property
            // block is read from the page itself): a row's relation is held to its database, a free
            // page's to `singlePages` when the plan has it (CS r4c), else to nothing.
            if (single) {
                if (singleByName.get(relation.notionName)?.proposedKind !== 'relation') {
                    ctx.addIssue({
                        code: 'custom',
                        message: "a free page's relation is not a relation property of singlePages",
                        path: [...at, 'relations', j, 'notionName'],
                    });
                }
                return;
            }
            if (database === undefined)
                return;
            const property = database.properties.find((p) => p.notionName === relation.notionName);
            if (property?.proposedKind !== 'relation') {
                ctx.addIssue({
                    code: 'custom',
                    message: "a relation's property is not a relation of the page's database",
                    path: [...at, 'relations', j, 'notionName'],
                });
            }
        });
    });
}
/** The plan of one export (2.A s3's output), validated with its references. */
export const ImportPlanV1 = z
    .strictObject({ ...head, pages: z.array(ImportPlanPageV1) })
    .superRefine((plan, ctx) => {
    checkDatabases(plan.databases, ctx);
    checkSinglePages(plan, ctx);
    checkPages(plan.pages, plan.databases, plan.singlePages, ctx);
});
/**
 * The plan without `pages` (DECISIONS 178): what `import.runs.plan` answers, for 2.B-2's mapping
 * step (databases, properties, options, relation targets and partners).
 */
export const ImportPlanOutlineV1 = z.strictObject(head).superRefine((plan, ctx) => {
    checkDatabases(plan.databases, ctx);
    checkSinglePages(plan, ctx);
});
//# sourceMappingURL=plan.js.map